import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";

const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

const LEAD_SOURCES = ["Referral", "Website", "Cold Call", "Trade Show", "Existing Customer", "Other"];

export default function ClientForm({ onSuccess, onCancel, initial, submitFn, submitLabel }) {
  const [form, setForm] = useState({
    company_name: initial?.company_name || "",
    contact_person: initial?.contact_person || "",
    phone: initial?.phone || "",
    email: initial?.email || "",
    billing_address: initial?.billing_address || "",
    city: initial?.city || "",
    state: initial?.state || "",
    pincode: initial?.pincode || "",
    gst_number: initial?.gst_number || "",
    lead_source: initial?.lead_source || "",
    shipping_addresses: initial?.shipping_addresses || [],
  });
  const [saving, setSaving] = useState(false);
  const [gstError, setGstError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validateGst = (v) => {
    const upper = (v || "").toUpperCase();
    if (!upper) return "GST number is required";
    if (!GST_REGEX.test(upper)) return "Invalid GST format. 15-char e.g. 27ABCDE1234F1Z5";
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validateGst(form.gst_number);
    if (err) { setGstError(err); return; }
    setGstError("");
    setSaving(true);
    try {
      const payload = { ...form, gst_number: form.gst_number.toUpperCase() };
      let res;
      if (submitFn) {
        res = await submitFn(payload);
      } else if (initial?.id) {
        res = await api.put(`/clients/${initial.id}`, payload);
      } else {
        res = await api.post("/clients", payload);
      }
      onSuccess?.(res.data);
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="client-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Company name *</Label>
          <Input required value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="client-company-input" className="mt-1" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Contact person *</Label>
          <Input required value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} data-testid="client-contact-input" className="mt-1" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Phone *</Label>
          <Input required value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="client-phone-input" className="mt-1" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email *</Label>
          <Input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="client-email-input" className="mt-1" />
        </div>
        <div className="col-span-2">
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Billing address *</Label>
          <Textarea required value={form.billing_address} onChange={(e) => set("billing_address", e.target.value)} data-testid="client-billing-input" className="mt-1" rows={2} />
        </div>
        <div className="col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
          {[["city", "City"], ["state", "State"], ["pincode", "PIN"]].map(([k, l]) => (
            <div key={k}>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{l}</Label>
              <Input value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`client-${k}-input`} className="mt-1" />
            </div>
          ))}
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">GST number *</Label>
          <Input
            required
            value={form.gst_number}
            onChange={(e) => { set("gst_number", e.target.value.toUpperCase()); setGstError(""); }}
            placeholder="27ABCDE1234F1Z5"
            data-testid="client-gst-input"
            className="mt-1 font-mono"
            maxLength={15}
          />
          {gstError && <div className="text-xs text-red-600 mt-1" data-testid="gst-error">{gstError}</div>}
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Lead source</Label>
          <Select value={form.lead_source} onValueChange={(v) => set("lead_source", v)}>
            <SelectTrigger className="mt-1" data-testid="client-source-select"><SelectValue placeholder="Select source" /></SelectTrigger>
            <SelectContent>
              {LEAD_SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        {onCancel && <Button type="button" variant="outline" onClick={onCancel} data-testid="client-cancel-btn">Cancel</Button>}
        <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="client-save-btn">
          {saving ? "Saving..." : (submitLabel || (initial?.id ? "Update client" : "Create client"))}
        </Button>
      </div>
    </form>
  );
}
